import React, { useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { S3_BUCKET, MY_BUCKET, REGION } from '../../services/s3';

//import md5 from 'md5';
//import friendly-url from 'friendly-url';
//var friendlyUrl = require('friendly-url');
import { addFile } from "../../store/modules/files/actions";

import ProgressBar from './ProgressBar';
import { Wrapper, Container, Button, Label } from './style';
import imageFiles from '../../assets/images/files.svg';

const UploadToS3 = ({ draggable = true }) => {
  const dispatch = useDispatch();
  const { files } = useSelector((state) => state.files);
  const [progress, setProgress] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [completedFiles, setCompletedFiles] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);

  const contentTypes = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'webp': 'image/webp',
    'svg': 'image/svg+xml'
  };

  const handleFileInput = (event) => {
    const fileList = Array.from(event.target.files);
    setSelectedFiles(fileList);
    setProgress({});
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const fileList = Array.from(event.dataTransfer.files);
    const imageFiles = fileList.filter(file => file.type.startsWith('image/'));
    setSelectedFiles(imageFiles);
    setProgress({});
  };

  const getByteSize = (number) => (number < 1024) && `${number} bytes`;

  const getKilobyteSize = (number) => {
    if(number >= 1024 && number < 1048576) {
      return `${(number / 1024).toFixed(1)} KB`;
    }
  };

  const getMegabyteSize = (number) => {
    if(number >= 1048576) {
      return `${(number / 1048576).toFixed(1)} MB`;
    }
  };

  const returnFileSize = (number) => {
    return getByteSize(number) ||
          getKilobyteSize(number) ||
          getMegabyteSize(number);
  };

  const getContentType = file => {
    const extension = file.split('.').pop();
    return contentTypes[extension];
  }

  const generateRandomString = (length = 6) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const sanitizeFileName = (fileName) => {
    // Tách tên file và extension
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex + 1);

    // Thay thế các ký tự đặc biệt bằng dấu gạch ngang
    const sanitizedName = name
      .replace(/[^a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF]/g, '-') // Thay thế ký tự đặc biệt, giữ lại chữ cái có dấu
      .replace(/-+/g, '-') // Thay thế nhiều dấu gạch ngang liên tiếp bằng một dấu
      .replace(/^-|-$/g, ''); // Loại bỏ dấu gạch ngang ở đầu và cuối

    // Thêm ký tự ngẫu nhiên để tránh trùng lặp
    const randomSuffix = generateRandomString(6);

    return `${sanitizedName}-${randomSuffix}.${extension}`;
  };

  const uploadFile = (file, fileIndex) => {
    if (!file) return null;

    const sanitizedFileName = sanitizeFileName(file.name);
    const fulln = sanitizedFileName;
    const params = {
      Body: file,
      Bucket: S3_BUCKET,
      Key: fulln,
      ContentType: getContentType(file.name)
    };

    MY_BUCKET
      .putObject(params)
      .on('httpUploadProgress', event => {
        const progressPercent = Math.round((event.loaded / event.total) * 100);
        setProgress(prev => ({
          ...prev,
          [fileIndex]: progressPercent
        }));
      })
      .send((err) => {
        if (err) {
          console.error(err);
          return `Error on try send file: ${ err.message }`;
        }

        // Tạo file object mới
        const newFile = {
          name: file.name,
          link: `https://${ S3_BUCKET }.s3.${ REGION }.amazonaws.com/${ fulln }`,
          size: returnFileSize(file.size),
          type: getContentType(file.name)
        };

        // Dispatch action để thêm file mới
        dispatch(addFile(newFile));

        // Cập nhật số file đã hoàn thành
        setCompletedFiles(prev => prev + 1);

        // Remove completed file from progress tracking
        setProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileIndex];
          return newProgress;
        });
      });
  };

  const uploadAllFiles = () => {
    if (selectedFiles.length === 0) return;

    // Khởi tạo trạng thái upload
    setIsUploading(true);
    setTotalFiles(selectedFiles.length);
    setCompletedFiles(0);

    selectedFiles.forEach((file, index) => {
      uploadFile(file, index);
    });

    // Clear selected files after starting upload
    setTimeout(() => {
      setSelectedFiles([]);
    }, 1000);
  };

  // Tính toán progress tổng thể
  const calculateOverallProgress = () => {
    if (totalFiles === 0) return 0;

    // Progress từ các file đã hoàn thành
    const completedProgress = (completedFiles / totalFiles) * 100;

    // Progress từ các file đang upload
    const currentProgress = Object.values(progress).reduce((sum, prog) => sum + prog, 0);
    const avgCurrentProgress = Object.keys(progress).length > 0
      ? (currentProgress / Object.keys(progress).length) / totalFiles * 100
      : 0;

    const totalProgress = Math.min(completedProgress + avgCurrentProgress, 100);

    // Kết thúc upload khi tất cả file đã hoàn thành
    if (completedFiles === totalFiles && totalFiles > 0) {
      setTimeout(() => {
        setIsUploading(false);
        setTotalFiles(0);
        setCompletedFiles(0);
      }, 1000);
    }

    return Math.round(totalProgress);
  };

  return (
    <Wrapper>
      <Container>
        <Label
          htmlFor="uploader"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            backgroundColor: isDragOver ? '#e3f2fd' : '',
            borderColor: isDragOver ? '#2196f3' : ''
          }}
        >
          <img src={imageFiles} alt="" />
          <span>Click or drop your images here</span>
          <input
            id="uploader"
            type="file"
            onChange={handleFileInput}
            accept="image/png, image/jpeg, image/svg+xml, image/webp"
            multiple
          />
          {selectedFiles.length === 0 ? (
            <p>No files selected</p>
          ) : (
            <div>
              <p>{selectedFiles.length} file(s) selected:</p>
              <ul style={{ textAlign: 'left', fontSize: '0.9rem', marginTop: '10px' }}>
                {selectedFiles.map((file, index) => (
                  <li key={index} style={{ marginBottom: '5px' }}>
                    {file.name} ({returnFileSize(file.size)})
                    {progress[index] && (
                      <span style={{ marginLeft: '10px', color: '#2196f3' }}>
                        {progress[index]}%
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Label>
        <Button onClick={uploadAllFiles} disabled={selectedFiles.length === 0 || isUploading}>
          <span>
            {isUploading
              ? `Uploading... (${completedFiles}/${totalFiles})`
              : `Upload ${selectedFiles.length > 0 ? `${selectedFiles.length} file(s)` : ''}`
            }
          </span>
        </Button>
      </Container>

      {/* Thanh progress tổng thể */}
      {(isUploading || totalFiles > 0) && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <p style={{ marginBottom: '10px', fontSize: '0.9rem', color: '#666' }}>
            Overall Progress: {completedFiles}/{totalFiles} files completed
          </p>
          <ProgressBar progress={calculateOverallProgress()} />
        </div>
      )}
    </Wrapper>
  );
};

export default UploadToS3;
